import React, { useEffect, useMemo, useRef, useState } from "react";
import { initials } from "../../utils/format";

const roleLabel = (user) => (user.user_type === "store_owner" ? "Owner" : user.is_store_admin ? "Admin" : "");

/* Multi-select popup for who a schedule item is assigned to. Works on a copy; Done hands the selection back. */
export default function AssigneePicker({ users, selected, onClose, onDone }) {
  const [picked, setPicked] = useState(() => new Set(selected));
  const [search, setSearch] = useState("");
  const searchRef = useRef(null);

  useEffect(() => {
    if (searchRef.current) searchRef.current.focus();
  }, []);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? users.filter((user) => user.name.toLowerCase().includes(term)) : users;
  }, [users, search]);

  const toggle = (id) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allVisiblePicked = visible.length > 0 && visible.every((user) => picked.has(user.id));

  const toggleAllVisible = () =>
    setPicked((current) => {
      const next = new Set(current);
      visible.forEach((user) => (allVisiblePicked ? next.delete(user.id) : next.add(user.id)));
      return next;
    });

  // keep the users' own order, not the order they were ticked in
  const done = () => onDone(users.filter((user) => picked.has(user.id)).map((user) => user.id));

  return (
    <div className="modal-backdrop no-print" style={{ zIndex: 1006 }}>
      <div className="modal-container modal-sm assign-picker" role="dialog" aria-modal="true" aria-labelledby="assign-picker-title">
        <div className="modal-header">
          <h3 id="assign-picker-title">👥 Assign To</h3>
          <button type="button" className="btn-icon-close" title="Close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="modal-body">
          <div className="assign-picker-tools">
            <input
              ref={searchRef}
              type="search"
              className="form-control"
              placeholder="Search users..."
              aria-label="Search users"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button type="button" className="btn-text-sm" onClick={toggleAllVisible} disabled={visible.length === 0}>
              {allVisiblePicked ? "Clear all" : "Select all"}
            </button>
          </div>

          <div className="assign-picker-list" role="group" aria-label="Users">
            {visible.length === 0 ? (
              <div className="assign-picker-empty">No users match "{search.trim()}"</div>
            ) : (
              visible.map((user) => {
                const role = roleLabel(user);
                return (
                  <label key={user.id} className={`assign-picker-row ${picked.has(user.id) ? "is-picked" : ""}`}>
                    <input type="checkbox" checked={picked.has(user.id)} onChange={() => toggle(user.id)} />
                    <span className="assign-avatar">{initials(user.name)}</span>
                    <span className="assign-picker-name">{user.name}</span>
                    {role && <span className="assign-role">{role}</span>}
                  </label>
                );
              })
            )}
          </div>
        </div>
        <div className="modal-footer">
          <span className="assign-picker-count">{picked.size ? `${picked.size} selected` : "Only you"}</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={done}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
